import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import StationSelector from './StationSelector';
import ManualSelectionModal from './ManualSelectionModal';
import '../styles/Header.css';

/**
 * Top header with:
 *   • Live global search (debounced, opens dropdown with hits)
 *   • User menu
 *
 * Prop:
 *   onNavigate(pageId, optionalContext)
 *     — called when user clicks a search result.
 *       If not provided, Header falls back to a sensible default
 *       (dispatches a custom event 'pg:navigate' other parts can listen to).
 */
export default function Header({ onMenuToggle, onNavigate, onOpenManual }) {
  const { user, logout } = useAuth();
  const [searchVal, setSearchVal] = useState('');
  const [results, setResults]     = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDrop, setShowDrop]   = useState(false);
  const [showMenu, setShowMenu]   = useState(false);
  const [showManual, setShowManual] = useState(false);
  const debounceRef = useRef(null);
  const searchRef   = useRef(null);

  const initial = user?.name ? user.name.charAt(0).toUpperCase() : 'U';

  const handleLogout = async () => {
    try {
      await fetch('/api/auth.php?action=logout', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${user?.token}` },
      });
    } catch (_) {}
    logout();
  };

  // ── Debounced search ───────────────────────────────────────
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = searchVal.trim();
    if (q.length < 2) {
      setResults([]);
      setShowDrop(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await api.search(q);
        setResults(data.results || []);
        setShowDrop(true);
      } catch (e) {
        setResults([]);
      }
      setSearching(false);
    }, 280);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchVal]);

  // ── Close dropdowns on outside click ──────────────────────
  useEffect(() => {
    if (!showMenu && !showDrop) return;
    const handler = (e) => {
      if (showMenu) setShowMenu(false);
      if (showDrop && searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDrop(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [showMenu, showDrop]);

  const gotoResult = (hit) => {
    setShowDrop(false);
    setSearchVal('');
    setResults([]);
    if (onNavigate) {
      onNavigate(hit.goto, hit);
    } else {
      // Fallback: dispatch an event so App.jsx (or anyone listening) can handle it
      window.dispatchEvent(new CustomEvent('pg:navigate', { detail: hit }));
    }
  };

  // Group results by type for the dropdown
  const grouped = results.reduce((acc, r) => {
    (acc[r.type] = acc[r.type] || []).push(r);
    return acc;
  }, {});
  const TYPE_ORDER = ['module', 'customer', 'supplier', 'bank', 'product', 'sale', 'purchase', 'payment', 'expense'];
  const TYPE_LABEL = {
    module:   'Navigation',
    customer: 'Customers',   supplier: 'Suppliers',  bank: 'Banks',
    product:  'Products',    sale:     'Sales',      purchase: 'Purchases',
    payment:  'Payments',    expense:  'Expenses',
  };

  return (
    <div className="header">
      {/* Hamburger — mobile only */}
      <button className="hamburger-btn" onClick={onMenuToggle} aria-label="Toggle menu">
        <span/><span/><span/>
      </button>

      <div className="header-left">
        <div className="search-box" ref={searchRef} onClick={e => e.stopPropagation()}>
          <input
            type="text"
            placeholder="Search customers, suppliers, bills..."
            value={searchVal}
            onChange={e => setSearchVal(e.target.value)}
            onFocus={() => { if (results.length > 0) setShowDrop(true); }}
          />
          <span className="search-icon">{searching ? '⏳' : '🔍'}</span>

          {/* Results dropdown */}
          {showDrop && (
            <div className="search-results">
              {results.length === 0 ? (
                <div className="search-empty">
                  {searching ? 'Searching...' : `No results for "${searchVal}"`}
                </div>
              ) : TYPE_ORDER.filter(t => grouped[t]).map(type => (
                <div key={type} className="search-group">
                  <div className="search-group-label">{TYPE_LABEL[type]}</div>
                  {grouped[type].map((hit, i) => (
                    <div
                      key={`${hit.type}-${hit.id}-${i}`}
                      className="search-hit"
                      onClick={() => gotoResult(hit)}
                    >
                      <span className="search-hit-icon">{hit.icon}</span>
                      <div className="search-hit-body">
                        <div className="search-hit-title">{hit.title}</div>
                        <div className="search-hit-sub">{hit.subtitle}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
        <StationSelector />

        <button 
          className="btn btn-sm btn-manual" 
          onClick={() => setShowManual(true)}
          style={{ 
            background: 'rgba(52, 152, 219, 0.1)', 
            color: '#3498db', 
            border: '1px solid #3498db',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontWeight: 600,
            borderRadius: 6
          }}
        >
          📘 User Manual
        </button>

        <div className="user-menu-wrap" onClick={e => e.stopPropagation()}>
          <div className="user-badge" onClick={() => setShowMenu(!showMenu)}>
            <span className="user-avatar">{initial}</span>
            <div className="user-info hide-xs">
              <span className="user-name">{user?.name || 'User'} ▾</span>
              <span className="user-email">{user?.email || ''}</span>
            </div>
          </div>
          {showMenu && (
            <div className="user-dropdown">
              <div className="ud-info">
                <div className="ud-name">{user?.name}</div>
                <div className="ud-email">{user?.email}</div>
                <div className="ud-station">⛽ {user?.station_name}</div>
              </div>
              <div className="ud-divider" />
              <button className="ud-logout" onClick={handleLogout}>
                🚪 Sign Out
              </button>
            </div>
          )}
        </div>
      </div>

      <ManualSelectionModal 
        isOpen={showManual} 
        onClose={() => setShowManual(false)} 
        onNavigate={onNavigate}
      />
    </div>
  );
}
